import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MagnifyingGlassIcon, PencilIcon } from '@heroicons/react/24/outline';
import publicCouponApi from '../../../services/publicCouponApi';
import { cn } from '../../../components/ui/_cn';
import FinBadge from '../../connect/finance/fin-badge';
import FinButton from '../../connect/finance/fin-button';
import { FinTableSkeleton } from '../../connect/finance/fin-skeleton';
import OrderReportPager from '../../finance/pages/order-report-pager';
import ChannelIcons from './campaign/ChannelIcons';
import { offerLines } from '../lib/campaign-offer';
import { ACTIVE_FILTER_STATUSES, ARCHIVED_FILTER_STATUSES, statusTag } from '../lib/campaign-status';

// The Public campaigns card (spec 2026-10-21 §2.2, handoff §1). The filter,
// the page and the search live in the URL (?view=archived&page=2&q=…), so a
// reload or a shared link keeps them, and /dashboard/campaigns/archive lands
// on ?view=archived. Search is the api's (`search`: name or code, any case),
// 300 ms after the last keystroke, and goes back to page 1.
export const PAGE_SIZE = 10;
export const SEARCH_DEBOUNCE_MS = 300;

export const LIST_COPY = {
  title: 'Public campaigns',
  search: 'Search name or code',
  filter: 'Campaign filter',
  pages: 'Campaign pages',
  loadError: 'Couldn’t load campaigns.',
  retry: 'Retry',
  emptyActive: 'No campaigns yet. Create one with New campaign.',
  emptyArchived: 'No archived campaigns.',
  noMatch: (query) => `No campaigns match “${query}”.`,
  showing: (first, lastRow, total) => `Showing ${first}–${lastRow} of ${total}`,
};

const VIEWS = {
  active: { label: 'Active', statuses: ACTIVE_FILTER_STATUSES },
  archived: { label: 'Archived', statuses: ARCHIVED_FILTER_STATUSES },
};

// Handoff §1: minmax(0,2fr) minmax(0,1.4fr) minmax(0,1.3fr) 56px 96px 32px.
// Status 96px holds "Inactive", "Archived" and "Draft"; Used 56px holds five
// digits. Below sm a row stacks: name | status, then the offer, then
// channels | used; the pencil goes (the whole row opens Edit).
const GRID =
  'grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1.5 sm:grid-cols-[minmax(0,2fr)_minmax(0,1.4fr)_minmax(0,1.3fr)_56px_96px_32px] sm:gap-y-0';

const totalOf = (res, rows) => (typeof res?.data?.totalRecords === 'number' ? res.data.totalRecords : rows.length);

const CampaignRow = ({ coupon, isLast, onEdit }) => {
  const { offer, kind } = offerLines(coupon);
  const tag = statusTag(coupon.status);
  const open = () => onEdit(coupon);
  return (
    <div
      role="row"
      tabIndex={0}
      onClick={open}
      onKeyDown={(event) => {
        // Only the row itself: an Enter on the pencil is that button's click.
        if (event.target === event.currentTarget && event.key === 'Enter') open();
      }}
      className={cn(
        GRID,
        'cursor-pointer items-center px-5 py-[11px] text-[13.5px] hover:bg-fin-hover focus:bg-fin-hover focus:outline-none',
        !isLast && 'border-b border-fin-hair',
      )}
    >
      <div role="cell" className="min-w-0">
        <p className="truncate font-semibold text-fin-ink" title={coupon.name}>
          {coupon.name}
        </p>
        <p className="truncate font-mono text-[12px] text-fin-muted">{coupon.code}</p>
      </div>
      <div role="cell" className="col-span-2 min-w-0 sm:col-span-1">
        <p className="truncate text-fin-ink">{offer}</p>
        <p className="truncate text-[12px] text-fin-muted">{kind}</p>
      </div>
      <div role="cell">
        <ChannelIcons platforms={coupon.platforms} />
      </div>
      {/* Pre-flight ruling R-11a: five digits on one line in the 56px column. */}
      <div role="cell" className="whitespace-nowrap text-right tabular-nums text-fin-ink">
        <span className="text-[12px] text-fin-muted sm:hidden">Used </span>
        <span className="tabular-nums">{coupon.usagecount ?? 0}</span>
      </div>
      <div role="cell" className="col-start-2 row-start-1 justify-self-end sm:col-start-auto sm:row-start-auto sm:justify-self-start">
        <FinBadge tone={tag.tone} label={tag.label} />
      </div>
      <div role="cell" className="hidden justify-end sm:flex">
        <button
          type="button"
          aria-label={`Edit ${coupon.name}`}
          onClick={(event) => {
            event.stopPropagation();
            open();
          }}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-fin-muted hover:bg-fin-neutral-pale hover:text-fin-ink"
        >
          <PencilIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

const CampaignList = ({ companyId, reloadKey = 0, onEdit }) => {
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'archived' ? 'archived' : 'active';
  const page = Math.max(1, parseInt(params.get('page') || '1', 10) || 1);
  const query = params.get('q') || '';

  const [search, setSearch] = useState(query);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ active: null, archived: null });
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const pageRequest = useRef(0);
  const countRequest = useRef(0);
  // The debounce timer fires up to 300 ms after the render that scheduled
  // it; always write through the latest setter (release-notes.js pattern),
  // so a filter clicked meanwhile is not erased.
  const setParamsRef = useRef(setParams);
  setParamsRef.current = setParams;

  const update = useCallback((patch, replace = false) => {
    setParamsRef.current(
      (prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(patch).forEach(([key, value]) => {
          if (value === null || value === undefined || value === '') next.delete(key);
          else next.set(key, String(value));
        });
        return next;
      },
      { replace },
    );
  }, []);

  // Typed search → ?q= after a pause, back to page 1.
  useEffect(() => {
    if (search === query) return undefined;
    const timer = setTimeout(() => update({ q: search.trim() ? search : null, page: null }, true), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // URL → box: back/forward changes ?q= without a keystroke.
  useEffect(() => {
    setSearch(query);
  }, [query]);

  const load = useCallback(async () => {
    if (!companyId) return;
    const id = ++pageRequest.current;
    setLoading(true);
    setFailed(false);
    try {
      const res = await publicCouponApi.list({
        companyid: companyId,
        statuses: VIEWS[view].statuses,
        pageNumber: page,
        pageSize: PAGE_SIZE,
        search: query,
      });
      if (id !== pageRequest.current) return;
      const data = res?.data?.data || [];
      const totalRecords = totalOf(res, data);
      setRows(data);
      setTotal(totalRecords);
      setCounts((prev) => ({ ...prev, [view]: totalRecords }));
    } catch {
      if (id !== pageRequest.current) return;
      setRows([]);
      setTotal(0);
      setFailed(true);
    } finally {
      if (id === pageRequest.current) setLoading(false);
    }
  }, [companyId, view, page, query]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  // The other filter's count (spec §2.2: read with PageSize 1), narrowed by
  // the same search.
  const other = view === 'active' ? 'archived' : 'active';
  useEffect(() => {
    if (!companyId) return;
    const id = ++countRequest.current;
    publicCouponApi
      .list({ companyid: companyId, statuses: VIEWS[other].statuses, pageNumber: 1, pageSize: 1, search: query })
      .then((res) => {
        if (id === countRequest.current) setCounts((prev) => ({ ...prev, [other]: totalOf(res, []) }));
      })
      .catch(() => {
        if (id === countRequest.current) setCounts((prev) => ({ ...prev, [other]: null }));
      });
  }, [companyId, other, query, reloadKey]);

  // A page past the end (the last row of page 3 archived): go to the last page.
  useEffect(() => {
    if (loading || failed || rows.length > 0 || total === 0 || page === 1) return;
    const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
    update({ page: lastPage === 1 ? null : lastPage }, true);
  }, [loading, failed, rows.length, total, page, update]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const first = (page - 1) * PAGE_SIZE + 1;
  const lastRow = Math.min(page * PAGE_SIZE, total);
  const empty = query ? LIST_COPY.noMatch(query) : view === 'archived' ? LIST_COPY.emptyArchived : LIST_COPY.emptyActive;

  let body;
  if (failed) {
    body = (
      <div className="flex min-h-[240px] flex-wrap items-center justify-center gap-3 border-t border-fin-border px-5 text-center">
        <p className="text-[13px] text-fin-red">{LIST_COPY.loadError}</p>
        <FinButton onClick={() => load()}>{LIST_COPY.retry}</FinButton>
      </div>
    );
  } else if (loading && rows.length === 0) {
    body = (
      <div className="border-t border-fin-border px-5">
        <FinTableSkeleton rows={PAGE_SIZE} />
      </div>
    );
  } else {
    body = (
      <div className="border-t border-fin-border">
        <div role="table" aria-label={LIST_COPY.title}>
          <div
            role="row"
            className={cn(GRID, 'hidden border-b border-fin-border bg-fin-recess px-5 py-2.5 text-[12px] font-medium text-fin-faint sm:grid')}
          >
            <div role="columnheader">Campaign</div>
            <div role="columnheader">Offer</div>
            <div role="columnheader">Channels</div>
            <div role="columnheader" className="text-right">Used</div>
            <div role="columnheader">Status</div>
            <div role="columnheader">
              <span className="sr-only">Edit</span>
            </div>
          </div>
          <div role="rowgroup">
            {rows.map((coupon, index) => (
              <CampaignRow key={coupon.id} coupon={coupon} isLast={index === rows.length - 1} onEdit={onEdit} />
            ))}
          </div>
        </div>
        {/* Sibling of role=table, not a child (recent-orders-table.js). */}
        {rows.length === 0 ? (
          <div className="flex min-h-[200px] items-center justify-center px-5 text-center">
            <p className="text-[13px] text-fin-muted">{empty}</p>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <section aria-labelledby="public-campaigns-title" className="overflow-hidden rounded-xl border border-fin-border bg-fin-surface">
      <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <h2 id="public-campaigns-title" className="text-[15px] font-bold text-fin-ink">
            {LIST_COPY.title}
          </h2>
          <div role="group" aria-label={LIST_COPY.filter} className="grid w-full grid-cols-2 rounded-lg bg-fin-hair p-0.5 sm:inline-flex sm:w-auto">
            {Object.keys(VIEWS).map((key) => (
              <button
                key={key}
                type="button"
                aria-pressed={view === key}
                onClick={() => {
                  if (view !== key) update({ view: key === 'archived' ? 'archived' : null, page: null });
                }}
                className={cn(
                  'inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-[13px] font-semibold',
                  view === key ? 'bg-fin-surface text-fin-ink shadow-sm' : 'text-fin-muted hover:text-fin-ink',
                )}
              >
                {VIEWS[key].label}
                {/* Pre-flight ruling R-11b (handoff §1): "Active (28)". The
                    space is its own text node, so the name reads "Active (28)";
                    the flex gap draws the visual space. */}
                {typeof counts[key] === 'number' ? (
                  <>
                    {' '}
                    <span className="tabular-nums text-fin-faint">({counts[key]})</span>
                  </>
                ) : null}
              </button>
            ))}
          </div>
        </div>
        <div className="relative w-full sm:w-[240px]">
          <MagnifyingGlassIcon
            className="pointer-events-none absolute inset-y-0 left-3 my-auto h-4 w-4 text-fin-faint"
            aria-hidden="true"
          />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={LIST_COPY.search}
            aria-label={LIST_COPY.search}
            className="h-9 w-full rounded-lg border border-fin-border-2 bg-fin-surface pl-9 pr-3 text-[13px] text-fin-ink outline-none placeholder:text-fin-faint focus:border-fin-blue"
          />
        </div>
      </div>
      {body}
      {!failed && total > 0 && rows.length > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-fin-border px-5 py-3">
          <p className="text-[13px] text-fin-muted">{LIST_COPY.showing(first, lastRow, total)}</p>
          {pageCount > 1 ? (
            <OrderReportPager
              label={LIST_COPY.pages}
              page={page}
              pageCount={pageCount}
              onChange={(next) => update({ page: next === 1 ? null : next })}
            />
          ) : null}
        </div>
      ) : null}
    </section>
  );
};

export default CampaignList;
